<script setup lang="js">
import {onMounted, ref} from "vue";
import {useRoute} from "vue-router";
import {useFetch} from "@/composables/useFetch.js";
import {uri} from "@/constants/config.js";
import Button from "@/base-components/Button";
import {useLogout} from "@/composables/useLogout.js";
import Lucide from "@/base-components/Lucide";
import BasePagination from "@/base-components/Table/BasePagination.vue";

const isLoading = ref(true)
const items = ref([])
const subItems = ref([])
const searchQuery = ref("")

const pageOptions = ref({
  count: 1,
  limit: 50,
  page: 1
})
const route = useRoute()
const type = route.params.id + 's'
const typeMap = {"tasks": "permissions", "roles": "tasks"}


async function fetchItems() {
  const {data, error} = await useFetch(uri.admin.rbac[type].list)
  if (error) return;
  items.value = data.data
}

async function fetchSubItems() {
  if (!typeMap[type]) return false
  const {data, error} = await useFetch(uri.admin.rbac[typeMap[type]].list, {
    params: {
      q: searchQuery.value,
      page: pageOptions.value.page,
      perPage: pageOptions.value.limit
    }
  })
  if (error) return;
  pageOptions.value.limit = data.meta.perPage
  pageOptions.value.count = data.meta.total
  subItems.value = data.data
}


function fetchData() {
  Promise.all([fetchItems(), fetchSubItems()])
      .then(() => isLoading.value = false)
}

async function promote(item, subItem) {
  isLoading.value = true
  await useFetch(uri.admin.rbac.promote, {body: {parent: item.name, child: subItem.name}, method: "POST"})
  await fetchItems()
  isLoading.value = false
}

async function revoke(item, subItem) {
  isLoading.value = true
  await useFetch(uri.admin.rbac.revoke, {body: {parent: item.name, child: subItem.name}, method: "POST"})
  await fetchItems()
  isLoading.value = false

}


onMounted(() => fetchData())
</script>

<template>
  <div class="intro-y">
    <div class="box p-5 mt-5">
      <free-style-shimmer height="75vh" width="100%" v-if="isLoading"/>
      <div class="box-content mt-2" v-if="!isLoading">
        <div class="intro-y col-span-2 md:col-span-1 xl:col-span-2 lg:col-span-2 mb-2">

          <input class="rounded" v-model="searchQuery" @change="fetchSubItems">
        </div>
        <table class="table-auto w-full border-collapse border border-gray-200 rounded-md">
          <thead class="bg-gray-100">
          <tr>
            <th class="border"></th>
            <th class="border px-4 py-1" v-for="item in items" :key="item.name">{{ item.name }}</th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="subItem in subItems"
              :key="subItem.name"
          >
            <td>{{ subItem.name }}</td>
            <td class="border px-4 py-1 text-center text-xs" v-for="item in items" :key="item.name">
              <Button
                  v-if="item.AsParent.find(x => x.childName === subItem.name)"
                  variant="outline-danger"
                  class="w-full text-xs align-top gap-2"
                  type="submit"
                  size="sm"
                  @click="revoke(item,subItem)"
              >
                <Lucide icon="CircleX" class="block  "/>
                {{ $t('button.revoke') }}
              </Button>
              <Button
                  v-else
                  variant="outline-primary"
                  class="w-full text-xs align-top gap-2"
                  type="submit"
                  size="sm"
                  @click="promote(item,subItem)"
              >
                <Lucide icon="CirclePlus" class="block  "/>
                {{ $t('button.promote') }}
              </Button>
            </td>
          </tr>
          </tbody>
          <tfoot>
          <tr>
            <td>
              <div
                  class="flex flex-col items-center p-5 text-center sm:flex-row sm:text-left text-slate-500"
              >
                <div>
                  <BasePagination
                      v-if="pageOptions.count>pageOptions.limit"
                      :page-options="pageOptions"
                      @page-change="fetchSubItems"
                  />
                </div>
                <div class="mt-2 sm:ml-auto sm:mt-0">
                </div>
              </div>
            </td>
          </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>

</template>

<style scoped>

</style>